'use client';

import { useEffect, useRef, useState } from 'react';

import { FA } from '@/shared/components/ui/icon/FA';
import { FB } from '@/shared/components/ui/icon/FB';
import type { MediaBlockAttrs } from './mediaBlock.types';
import s from './MediaBlock.module.scss';

const AUDIO_PLAY_EVENT = 'mediablock:audio-play';

interface Props {
  attrs: MediaBlockAttrs;
}

export const MediaBlock = ({ attrs }: Props) => {
  const [videoPlaying, setVideoPlaying] = useState(false);

  if (attrs.mediaType === 'video' && attrs.videoId) {
    return (
      <div className={s.block}>
        <div className={s.inner}>
          {(!!attrs.title || !!attrs.description) && (
            <div className={s.videoHeader}>
              <div>
                {!!attrs.title && <div className={s.videoTitle}>{attrs.title}</div>}
                {!!attrs.description && <div className={s.description}>{attrs.description}</div>}
              </div>
              <FB icon="fa-youtube" className={s.youtubeIcon} />
            </div>
          )}
          <div className={s.videoContainer}>
            {videoPlaying ? (
              <iframe
                src={`https://www.youtube-nocookie.com/embed/${attrs.videoId}?autoplay=1&rel=0&modestbranding=1`}
                className={s.iframe}
                allow="autoplay; encrypted-media"
                allowFullScreen
              />
            ) : (
              <div onClick={() => setVideoPlaying(true)}>
                <img
                  alt={attrs.title || 'YouTube video'}
                  src={`https://i.ytimg.com/vi/${attrs.videoId}/hqdefault.jpg`}
                  className={s.thumbnail}
                  loading="lazy"
                />
                <div className={s.playOverlay}>
                  <div className={s.playButton}>
                    <FA icon="fa-play" theme="fas" className={s.playIcon} />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (attrs.mediaType === 'audio' && attrs.audioUrl) {
    return (
      <div className={s.blockLight}>
        <div className={s.inner}>
          <div className={s.audioRow}>
            <div className={s.audioBadge}>
              <FA icon="fa-waveform-lines" theme="fad" className={s.audioBadgeIcon} />
            </div>
            <div className={s.audioContent}>
              <div className={s.audioTitle}>{attrs.title || attrs.audioTitle || 'Audio file'}</div>
              {!!attrs.description && <div className={s.audioDescription}>{attrs.description}</div>}
              <AudioPlayer src={attrs.audioUrl} className={s.audioPlayer} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

/** Pauses other audio players on the page when this one starts playing. */
const AudioPlayer = ({ src, className }: { src: string; className?: string }) => {
  const ref = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const handlePlay = () => {
      window.dispatchEvent(new CustomEvent(AUDIO_PLAY_EVENT, { detail: el }));
    };

    const handleGlobalPlay = (e: Event) => {
      if ((e as CustomEvent).detail !== el) el.pause();
    };

    el.addEventListener('play', handlePlay);
    window.addEventListener(AUDIO_PLAY_EVENT, handleGlobalPlay);
    return () => {
      el.removeEventListener('play', handlePlay);
      window.removeEventListener(AUDIO_PLAY_EVENT, handleGlobalPlay);
    };
  }, []);

  return <audio ref={ref} src={src} controls className={className} preload="metadata" />;
};
